import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Wait this long for the role claim to land before showing the
 * "awaiting role" screen. The onUserCreated Cloud Function usually
 * stamps the claim within a few hundred ms; we force a token refresh
 * once after this grace period to pick up a freshly-set claim that
 * the client cached without.
 */
const ROLE_GRACE_MS = 1500;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, role, loading, logout } = useAuth();
  const [graceElapsed, setGraceElapsed] = useState(false);

  // Once auth has resolved a user but no role claim has appeared,
  // give the onUserCreated trigger a brief window then force a token
  // refresh — claims set server-side aren't visible to the client
  // until the next refresh.
  useEffect(() => {
    if (loading || !currentUser || role != null) {
      setGraceElapsed(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        await auth.currentUser?.getIdToken(true); // force refresh
      } catch {
        // swallow — the AuthContext listener will re-evaluate on
        // its own and we'll fall through to the awaiting-role screen
      }
      setGraceElapsed(true);
    }, ROLE_GRACE_MS);
    return () => clearTimeout(t);
  }, [loading, currentUser, role]);

  // Avoid a flash-of-redirect while auth state is still resolving.
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Signed in but no role claim yet. Either the onUserCreated trigger
  // hasn't run/finished, or it failed entirely. The Firestore rules
  // require `role`, so without it the app is empty + filled with
  // permission-denied errors — not a viable UX. Block here instead.
  if (role == null) {
    if (!graceElapsed) {
      return (
        <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Account awaiting setup
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Your account exists but hasn't been assigned a role yet.
            That usually clears within a minute of signup — if it
            doesn't, an admin needs to provision you manually.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            Signed in as <span className="font-medium">{currentUser.email}</span>
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { void logout(); }}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm font-medium"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
